import { Divider } from '@advanza/ui'
import { SystemLog } from '@trustoo/types/entity/systemLog'
import Card from 'components/Card'
import ExpectationSettingEntity, {
    EXP_SET_BOUND,
} from 'components/expectationsettings/ExpectationSettingEntity'
import LogsList, {
    Changes,
    formatMessageByLogType,
    LogUnion,
    NOT_RENDERED,
} from 'components/logs/LogsList'
import TableList from 'components/TableList'
import { useListPerCountry } from 'hooks/listHooks'
import { useOptions } from 'hooks/optionHooks'
import { useService } from 'hooks/serviceHooks'
import Option from 'interfaces/Options'
import { schema } from 'normalizr'

const useExpectationSettingsList = (filterId = 'all', defaultOptions = {}) =>
    useListPerCountry(
        filterId,
        {
            url: 'office/expectation-settings/get-list',
            schema: [
                new schema.Entity(
                    'expectationSettings',
                    {},
                    { idAttribute: 'expectationsetting_id' },
                ),
            ],
            reducerKey: 'expectationSettings',
        },
        {
            ...defaultOptions,
            sorting: ['serviceName', 'ASC'],
        },
    )

const ExpectationSettingsList = ({ filterId = 'all' }) => {
    const {
        entities: { expectationSettings = {} },
        extra: { definitionsExpectationSettings = {} } = {},
        ...list
    } = useExpectationSettingsList(filterId)
    const serviceId: number | undefined = list.filter.serviceIds?.[0]
    const { service } = useService(serviceId)
    const options = useOptions(serviceId).options as {
        [optionId: number]: Option
    }

    const renderRow = (expectationSettingId: number) => {
        const expectationSetting = expectationSettings[expectationSettingId]
        const isMainExpSet = expectationSettingId < EXP_SET_BOUND

        return {
            cols: Object.keys(definitionsExpectationSettings).map((key) => {
                if (key === 'theSlug') {
                    return (
                        !isMainExpSet &&
                        expectationSetting.theSlug &&
                        '<' + expectationSetting.theSlug + '>'
                    )
                } else if (key === 'region_optimizer_use_avg_coverage') {
                    if (!isMainExpSet) {
                        return null
                    }

                    return expectationSetting.region_optimizer_use_avg_coverage ? 'Yes' : 'No'
                }

                return expectationSetting[key]
            }),
            expandable: <ExpectationSettingEntity expectationSettingId={expectationSettingId} />,
        }
    }

    const renderCol = (log: LogUnion, col: string) => {
        const systemLog = log as SystemLog

        if (col === 'foreign_key') {
            if (!systemLog.foreign_key) {
                return '?'
            }

            if (systemLog.foreign_key < EXP_SET_BOUND) {
                return `${systemLog.foreign_key} service: ${service?.name}`
            }

            return `${systemLog.foreign_key} option: ${options[systemLog.foreign_key - EXP_SET_BOUND]?.value}`
        } else if (col === 'message') {
            const preferredFieldOrder = Object.keys(definitionsExpectationSettings).map((field) =>
                field.replace(/option(\d?)Name/, 'filteroption$1_id'),
            )

            const logCopy = { ...log }
            logCopy.message_json = Object.fromEntries(
                Object.entries((log.message_json || {}) as Changes)
                    .sort(
                        ([fieldA], [fieldB]) =>
                            preferredFieldOrder.indexOf(fieldA) -
                            preferredFieldOrder.indexOf(fieldB),
                    )
                    .map(([field, change]) => {
                        if (field.match(/filteroption\d?_id/)) {
                            const transformOptionId = (optionId: unknown) =>
                                typeof optionId === 'number' && optionId
                                    ? `${options[optionId]?.value} (${optionId})`
                                    : '-'
                            change = {
                                old: transformOptionId(change.old),
                                new: transformOptionId(change.new),
                            }
                        }

                        return [field, change]
                    }),
            )

            return formatMessageByLogType(logCopy)
        }

        return NOT_RENDERED
    }

    return (
        <>
            <TableList
                definitions={definitionsExpectationSettings}
                renderRow={renderRow}
                addListFeatures
                showServicesSelect={{
                    multiple: false,
                    placeholder: 'Select a service',
                    emptyOption: 'Select a service',
                    hidePlaceholder: true,
                }}
                {...list}
            />{' '}
            <Divider m />
            {serviceId && (
                <Card header="Change history" icon="history" useToggle>
                    <LogsList
                        isProLogs={false}
                        filterId={`expectation_settings_${serviceId ?? 'all'}`}
                        options={{
                            relation: 'expectation_settings',
                            foreignKey: serviceId,
                        }}
                        hideLogTypeSelection
                        showCols={['foreign_key', 'message', 'created', 'user_id']}
                        renderCol={renderCol}
                    />
                </Card>
            )}
        </>
    )
}

export default ExpectationSettingsList
